import { forwardRef, useImperativeHandle } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Datebox, Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { DMRDrug, DMRDrugListResponse } from "@/App/api/drug";
import {
  DirectMemberReimbursementNoResults,
  DirectMemberReimbursementPrescriptionVaccineEquipmentCard,
} from "@/App/pages/DirectMemberReimbursement";
import { validateDecimal, validateInteger } from "@/App/utils/validation";

interface DrugSelectionAndDetailsProps {
  readonly largeFieldsColumns: number;
  readonly smallFieldsColumns: number;
  readonly prescriptionVaccineEquipmentResults?: DMRDrugListResponse;
  readonly isLoadingPrescriptionVaccineEquipmentResults?: boolean;
  readonly handleSelectDrug?: () => void;
}
const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementClaimDetailsForm";

type DrugSelectionAndDetailsForm = {
  drugDetails: {
    ndc: string;
    drug: DMRDrug;
    purchased_date: string;
    prescription_number: string;
    quantity?: number;
    days_supply?: number;
    total_paid: number;
    admin_fee: number;
  }[];
};

export type DrugSelectionAndDetailsFormRef = {
  isValid: boolean;
  submit: () => Promise<DrugSelectionAndDetailsForm>;
  getDrugDetailsCount: () => number;
};

export const DirectMemberReimbursementDrugSelectionAndDetails = forwardRef<DrugSelectionAndDetailsFormRef, DrugSelectionAndDetailsProps>(
  (
    {
      largeFieldsColumns,
      smallFieldsColumns,
      prescriptionVaccineEquipmentResults,
      isLoadingPrescriptionVaccineEquipmentResults,
      handleSelectDrug = () => {},
    },
    ref,
  ) => {
    const { t: translate } = useTranslation();

    const drugSelectionAndDetailsForm = useForm<DrugSelectionAndDetailsForm>();
    const drugDetailsFieldArray = useFieldArray({ control: drugSelectionAndDetailsForm.control, name: "drugDetails" });

    useImperativeHandle(ref, () => ({
      isValid: drugSelectionAndDetailsForm.formState.isValid,
      submit: () =>
        new Promise<DrugSelectionAndDetailsForm>((resolve) => {
          drugSelectionAndDetailsForm.handleSubmit(
            () => resolve(drugSelectionAndDetailsForm.getValues()),
            () => resolve(null),
          )();
        }),
      getDrugDetailsCount: () => drugSelectionAndDetailsForm.getValues("drugDetails")?.length ?? 0,
    }));

    return (
      <Form form={drugSelectionAndDetailsForm}>
        <Stack>
          <Grid spacing="medium">
            <Grid.Span columns={largeFieldsColumns}>
              <LoadingGroup loading={isLoadingPrescriptionVaccineEquipmentResults}>
                <LoadingGroup.Placeholder fallback={<LoadingSkeleton count={1} style={{ height: "100px" }} />}>
                  {prescriptionVaccineEquipmentResults?.results.length > 0 && (
                    <Stack spacing="medium">
                      <Heading>{translate(`${TRANSLATION_PATH}.searchResults`)}</Heading>
                      {prescriptionVaccineEquipmentResults?.results.map(({ drug }, index: number) => (
                        <DirectMemberReimbursementPrescriptionVaccineEquipmentCard
                          key={`dmr-drug-card-${index}`}
                          dmrDrug={drug}
                          isSelected={false}
                          onSelect={() => {
                            drugDetailsFieldArray.prepend({
                              ndc: drug.ndc,
                              drug: drug,
                              purchased_date: null,
                              prescription_number: null,
                              quantity: null,
                              days_supply: null,
                              total_paid: null,
                              admin_fee: null,
                            });
                            handleSelectDrug();
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  {prescriptionVaccineEquipmentResults?.results.length == 0 && <DirectMemberReimbursementNoResults isSingular={true} />}
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Grid.Span>
          </Grid>
          {drugDetailsFieldArray.fields.length > 0 && <Heading>{translate(`${TRANSLATION_PATH}.selectedPrescriptionHeading`)}</Heading>}
          {drugDetailsFieldArray.fields.map((field, index) => (
            <Stack key={field.id}>
              <Grid>
                <Grid.Span columns={largeFieldsColumns}>
                  <DirectMemberReimbursementPrescriptionVaccineEquipmentCard
                    key={`dmr-drug-card-selected-${index}`}
                    dmrDrug={field.drug}
                    isSelected={true}
                    onRemove={() => drugDetailsFieldArray.remove(index)}
                  />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Datebox.Field
                    name={`drugDetails.${index}.purchased_date`}
                    label={translate(`${TRANSLATION_PATH}.purchasedFillDate`)}
                    required
                  />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field
                    name={`drugDetails.${index}.prescription_number`}
                    label={translate(`${TRANSLATION_PATH}.prescriptionRxNumber`)}
                    required
                  />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field
                    name={`drugDetails.${index}.quantity`}
                    label={translate(`${TRANSLATION_PATH}.quantity`)}
                    validator={(value) => {
                      if (value) {
                        return validateDecimal(value || "", translate, { maxDecimalPlaces: 4, greaterThan: 0 });
                      } else {
                        return undefined;
                      }
                    }}
                  />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field
                    name={`drugDetails.${index}.days_supply`}
                    label={translate(`${TRANSLATION_PATH}.daysSupply`)}
                    validator={(value) => {
                      if (value) {
                        return validateInteger(value || "", translate, { greaterThan: 0 });
                      } else {
                        return undefined;
                      }
                    }}
                  />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field
                    prefix="$"
                    name={`drugDetails.${index}.total_paid`}
                    label={translate(`${TRANSLATION_PATH}.totalPaid`)}
                    instructions={translate(`${TRANSLATION_PATH}.noSpecialCharactersInstruction`)}
                    validator={(value) => validateDecimal(value || "", translate, { maxDecimalPlaces: 2, greaterThan: 0 })}
                    required
                  />
                </Grid.Span>
                <Grid.Break />
                <Grid.Span columns={smallFieldsColumns}>
                  <Textbox.Field
                    prefix="$"
                    name={`drugDetails.${index}.admin_fee`}
                    label={translate(`${TRANSLATION_PATH}.adminFee`)}
                    instructions={translate(`${TRANSLATION_PATH}.noSpecialCharactersInstruction`)}
                    validator={(value) => validateDecimal(value || "", translate, { maxDecimalPlaces: 2, nonNegative: true })}
                    required
                  />
                </Grid.Span>
                <Grid.Break />
              </Grid>
            </Stack>
          ))}
        </Stack>
      </Form>
    );
  },
);
